import { useState, type ChangeEvent, type FC, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/primitives/Button';
import { Container } from '@/components/layout/Container';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { CONTACT_SALES_EMAIL, CONTACT_SALES_EMAIL_HREF } from '@/lib/contact';
import styles from './Login.module.css';

interface LoginFormState {
  email: string;
  password: string;
  rememberMe: boolean;
}

const INITIAL_STATE: LoginFormState = {
  email: '',
  password: '',
  rememberMe: false,
};

const Login: FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<LoginFormState>(INITIAL_STATE);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    // Placeholder: auth is not yet wired. Intentionally a no-op for now.
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setState((s) => ({ ...s, email: event.target.value }));
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setState((s) => ({ ...s, password: event.target.value }));
  };

  const handleRememberChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setState((s) => ({ ...s, rememberMe: event.target.checked }));
  };

  return (
    <main className={styles.root}>
      <Container size="narrow">
        <div className={styles.layout}>
          <aside className={styles.aside}>
            <h2 className={styles.asideTitle}>{t('login.aside.title')}</h2>
            <p className={styles.asideBody}>{t('login.aside.body')}</p>
            <ul className={styles.asideList}>
              <li>{t('login.aside.list.auditTrail')}</li>
              <li>{t('login.aside.list.reserveAttestations')}</li>
              <li>{t('login.aside.list.policyAsCode')}</li>
            </ul>
          </aside>

          <section className={styles.formCard}>
            <Eyebrow>{t('login.title')}</Eyebrow>
            <p className={styles.subtitle}>{t('login.subtitle')}</p>

            <form onSubmit={handleSubmit} className={styles.form} noValidate>
              <label className={styles.field}>
                <span className={styles.label}>{t('login.form.emailLabel')}</span>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  className={styles.input}
                  placeholder={t('login.form.emailPlaceholder')}
                  value={state.email}
                  onChange={handleEmailChange}
                />
              </label>

              <label className={styles.field}>
                <span className={styles.label}>{t('login.form.passwordLabel')}</span>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  className={styles.input}
                  placeholder={t('login.form.passwordPlaceholder')}
                  value={state.password}
                  onChange={handlePasswordChange}
                />
              </label>

              <div className={styles.row}>
                <label className={styles.remember}>
                  <input
                    type="checkbox"
                    checked={state.rememberMe}
                    onChange={handleRememberChange}
                  />
                  <span>{t('login.form.rememberMe')}</span>
                </label>
                <a href="#forgot" className={styles.forgot}>
                  {t('login.form.forgotPassword')}
                </a>
              </div>

              <Button type="submit" variant="secondary" size="lg" fullWidth>
                {t('login.form.submit')}
              </Button>

              {/* Escape hatch directly under the sign-in CTA — a
                  sign-in page is a dead end for anyone who just wanted
                  to browse. Styled as the primary button so every
                  "back to home" control on the site shares one
                  treatment (Example page, 404 page, sign-in page). */}
              <Button as="link" to="/" variant="primary" size="lg">
                {t('login.form.backToHome')}
              </Button>
            </form>

            <div className={styles.divider}>
              <span>{t('login.sso.divider')}</span>
            </div>

            <div className={styles.sso}>
              <Button type="button" variant="secondary" size="md" fullWidth>
                {t('login.sso.google')}
              </Button>
              <Button type="button" variant="secondary" size="md" fullWidth>
                {t('login.sso.microsoft')}
              </Button>
            </div>

            <div className={styles.note}>
              <p className={styles.noteTitle}>{t('login.note.title')}</p>
              <p className={styles.noteBody}>{t('login.note.body')}</p>
            </div>

            <p className={styles.footer}>{t('login.footer')}</p>
            <p className={styles.contactFooter}>
              {t('login.contactPrompt')}{' '}
              <a href={CONTACT_SALES_EMAIL_HREF} className={styles.contactLink}>
                {CONTACT_SALES_EMAIL}
              </a>
            </p>
          </section>
        </div>
      </Container>
    </main>
  );
};

export default Login;
